import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'

import { API_URL } from '@/constants/env'

import { createFetchError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  pursuitQueryKeys,
  pursuitStagesQueryKeys,
  pursuitsQueryKeys,
} from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'
import type { PursuitStage, ReplacePursuitStages } from '@/types/pursuit/Stage'

interface UseUpsertPursuitStagesArgs {
  orgId: UUID | ''
}

const replacePursuitStages = async (
  orgId: UUID | '',
  request: ReplacePursuitStages,
  accessToken: string,
): Promise<PursuitStage[]> => {
  if (!orgId) {
    throw new Error('Org ID must be provided')
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/stages`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw await createFetchError(res)
  }

  const replaced = (await res.json()) as PaginatedResponse<PursuitStage>
  return replaced.data
}

export const useUpsertPursuitStages = ({
  orgId,
}: UseUpsertPursuitStagesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<PursuitStage[], Error, ReplacePursuitStages>({
    mutationFn: (request) => replacePursuitStages(orgId, request, accessToken),
    onError: (err) => {
      // Refetch in case the save failed because another user changed the stages.
      queryClient.invalidateQueries({
        queryKey: pursuitStagesQueryKeys.organization({ orgId }),
      })
      handleError(err)
    },
    onSuccess: (data) => {
      const stageIds = new Set(data.map((stage) => stage.id))
      const stageMap = new Map(data.map((stage) => [stage.id, stage]))

      const pursuitsQueryKey = pursuitsQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<Pursuit>>(
        pursuitsQueryKey,
        (oldData) => {
          if (!oldData) return oldData

          const minStage = data.reduce((min, stage) =>
            stage.index < min.index ? stage : min,
          )

          return produce(oldData, (draft) => {
            draft.data.forEach((pursuit) => {
              if (!stageIds.has(pursuit.stage.id)) {
                pursuit.stage = minStage
              } else {
                pursuit.stage = stageMap.get(pursuit.stage.id) ?? pursuit.stage
              }
            })
          })
        },
      )
      // The patch above only covers the exact unparameterized key; filtered/
      // paginated pursuits queries still hold stale embedded stage objects.
      queryClient.invalidateQueries({ queryKey: pursuitsQueryKey })
      // Open pursuit pages hold their own copy of the pursuit's stage.
      queryClient.invalidateQueries({ queryKey: pursuitQueryKeys.all() })
      const key = pursuitStagesQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<PursuitStage>>(key, {
        count: data.length,
        has_more: false,
        data: data,
      })
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({
        queryKey: pursuitsQueryKeys.organizationsOverviewAll(),
      })
      queryClient.invalidateQueries({
        queryKey: pursuitStagesQueryKeys.organizationsOverviewAll(),
      })
    },
  })
}
